import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, BellOff, Loader2, AlertCircle, Send, Check, Utensils, Cake, Moon } from 'lucide-react'
import { isOwner } from '../lib/owner'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { useNotificationPreferences } from '../hooks/useNotificationPreferences'
import { useStandalone } from '../hooks/useStandalone'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../lib/utils'

// Per-kind tile metadata for the additional notification kinds. Excludes
// daily_checkin which is the original toggle at the top.
const KIND_TILES = [
  { kind: 'meals',    icon: Utensils, hasTime: true,  accent: 'text-red-500' },
  { kind: 'birthday', icon: Cake,     hasTime: false, accent: 'text-pink-500' },
]

// Owner-only gate for the push-debug buttons (Send test / Show local).
// They're useful while the notification pipeline is still being tuned but
// don't belong in front of regular users. Hard-coded against the project
// owner's auth user_id — there's only one such user, so a list/role
// system would be overkill.
const DEBUG_BUTTONS_OWNER_ID = 'cb846c7b-e9f3-4d5c-8390-9598c4ae4ece'

function isIOS() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

function pad(n) {
  return String(n).padStart(2, '0')
}

export default function ReminderSettings() {
  const { t } = useTranslation()
  const standalone = useStandalone()
  const { user } = useAuth()
  const showDebugButtons = user?.id === DEBUG_BUTTONS_OWNER_ID
  const {
    permission,
    enabled,
    reminderHour,
    reminderMinute,
    timezone,
    busy,
    error,
    enable,
    disable,
    sendTest,
    updateTime,
    supported,
  } = usePushSubscription()
  const {
    get: getPref,
    setEnabled: setPrefEnabled,
    setTime: setPrefTime,
    busy: prefsBusy,
  } = useNotificationPreferences()

  const [time, setTime] = useState(`${pad(reminderHour)}:${pad(reminderMinute)}`)
  const [editing, setEditing] = useState(false)
  // Test-button feedback. `null` while idle; { ok, message } briefly after
  // a tap so the user sees the outcome without opening function logs.
  const [testResult, setTestResult] = useState(null)
  const [testing, setTesting] = useState(false)

  const handleSendTest = async () => {
    if (testing || busy) return
    setTesting(true)
    setTestResult(null)
    const result = await sendTest()
    setTesting(false)
    if (result.ok) {
      setTestResult({ ok: true, message: t('reminders.testSent') })
    } else {
      // Surface the actual reason — VAPID error / no subscriptions / etc.
      const detail = result?.data?.failures?.[0]?.message || result.error || 'unknown'
      setTestResult({ ok: false, message: t('reminders.testFailed', { detail }) })
    }
    // Auto-clear after a few seconds so it doesn't sit there forever.
    setTimeout(() => setTestResult(null), 6000)
  }

  // Diagnostic: fire a notification directly via the SW, skipping the
  // push pipeline entirely. If this shows but "Send test" doesn't, the
  // problem is push delivery (APNs / VAPID / subscription). If neither
  // shows, the problem is iOS-level notification handling for this PWA
  // — even when the system settings look correct.
  const handleLocalTest = async () => {
    if (testing) return
    setTesting(true)
    setTestResult(null)
    try {
      const reg = await navigator.serviceWorker?.ready
      if (!reg) {
        setTestResult({ ok: false, message: t('reminders.localTestFailed', { detail: 'no service worker' }) })
        return
      }
      await reg.showNotification('Phoenix — Local test', {
        body: t('reminders.localTestBody'),
        icon: '/icons/icon-192.png',
        tag: `phoenix-local-${Date.now()}`,
        renotify: true,
      })
      setTestResult({ ok: true, message: t('reminders.localTestSent') })
    } catch (e) {
      setTestResult({
        ok: false,
        message: t('reminders.localTestFailed', { detail: e?.message || 'unknown' }),
      })
    } finally {
      setTesting(false)
      setTimeout(() => setTestResult(null), 6000)
    }
  }

  // Sync local state from the server value, but only when the user is NOT
  // actively interacting with the time picker. Otherwise iOS dismisses its
  // native wheel picker on every value-prop change.
  useEffect(() => {
    if (!editing) {
      setTime(`${pad(reminderHour)}:${pad(reminderMinute)}`)
    }
  }, [reminderHour, reminderMinute, editing])

  // Hard stops:
  if (!supported) return null
  if (isIOS() && !standalone) {
    return (
      <div className="px-4 py-3 border-b border-border/60">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Bell className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{t('reminders.iosInstallRequired')}</p>
        </div>
      </div>
    )
  }

  const blocked = permission === 'denied'

  const handleToggle = async () => {
    if (busy) return
    if (enabled) {
      await disable()
    } else {
      const [h, m] = time.split(':').map(Number)
      await enable(h || 0, m || 0)
    }
  }

  // onChange only mutates local state — committing to Supabase here would
  // cause a re-render mid-spin and iOS dismisses the time picker.
  const handleTimeChange = (e) => {
    setTime(e.target.value)
  }

  // Commit the new time when the user finishes interacting (blur / picker
  // closes). Only writes if it actually changed.
  const handleTimeCommit = async () => {
    setEditing(false)
    if (!enabled) return
    const [h, m] = time.split(':').map(Number)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return
    if (h === reminderHour && m === reminderMinute) return
    await updateTime(h, m)
  }

  return (
    <div className="px-4 py-3 border-b border-border/60">
      <div className="flex items-center gap-3">
        {enabled ? (
          <Bell className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
        ) : (
          <BellOff className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground font-medium">{t('reminders.title')}</p>
          {!enabled && !blocked && (
            <p className="text-[11px] text-muted-foreground truncate">
              {t('reminders.description')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={busy || blocked}
          aria-label={enabled ? t('reminders.disable') : t('reminders.enable')}
          className={cn(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0',
            enabled ? 'bg-app-primary-500' : 'bg-muted',
            (busy || blocked) && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              enabled ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1'
            )}
          />
        </button>
      </div>

      {blocked && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{t('reminders.permissionBlocked')}</span>
        </div>
      )}

      {/* Diagnostic status strip — visible to ALL users so a partner
          can immediately see whether their enable attempt actually
          landed. Three checks: browser permission, DB subscription
          row, and a one-tap local-notification test that exercises
          the service worker without touching the server pipeline.
          If "subscribed" is ✗ but "permission" is ✓ the user has
          tapped Allow but the SW subscribe step failed silently —
          tapping Enable again usually fixes it. */}
      <div className="mt-3 pt-3 border-t border-border/60 space-y-1.5">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium px-0.5" dir="auto">
          {t('reminders.diagnostics.title')}
        </p>
        <DiagnosticRow
          label={t('reminders.diagnostics.permission')}
          state={permission === 'granted' ? 'ok' : permission === 'denied' ? 'bad' : 'pending'}
          detail={t(`reminders.diagnostics.permission_${permission}`)}
        />
        <DiagnosticRow
          label={t('reminders.diagnostics.subscribed')}
          state={enabled ? 'ok' : 'bad'}
          detail={t(`reminders.diagnostics.subscribed_${enabled ? 'yes' : 'no'}`)}
        />
        <button
          type="button"
          onClick={handleLocalTest}
          disabled={testing}
          className={cn(
            'mt-1 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium',
            'bg-muted text-foreground hover:bg-accent transition-colors',
            testing && 'opacity-50 cursor-not-allowed'
          )}
        >
          <Bell className="w-3 h-3" />
          {t('reminders.diagnostics.testLocal')}
        </button>
        {testResult && (
          <p
            className={cn(
              'text-[11px]',
              testResult.ok ? 'text-emerald-500' : 'text-red-500'
            )}
            dir="auto"
          >
            {testResult.message}
          </p>
        )}
      </div>

      {enabled && (
        <>
          <div className="mt-2 flex items-center gap-2">
            <label className="text-[11px] text-muted-foreground">
              {t('reminders.time')}
            </label>
            <input
              type="time"
              value={time}
              onFocus={() => setEditing(true)}
              onChange={handleTimeChange}
              onBlur={handleTimeCommit}
              disabled={busy}
              dir="ltr"
              className="h-8 px-2 rounded border border-input bg-background text-foreground font-mono text-xs focus:outline-none"
            />
            {timezone && (
              <span className="text-[10px] text-muted-foreground font-mono truncate" title={timezone}>
                {timezone}
              </span>
            )}
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </div>

          {/* Per-kind notifications (meals / water / birthday). Each tile
              is its own toggle + optional time picker. Birthday has no
              time picker since it fires at noon user-local. */}
          <div className="mt-3 space-y-1.5 pt-3 border-t border-border/60">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium px-0.5" dir="auto">
              {t('reminders.moreKinds')}
            </p>
            {KIND_TILES.map(({ kind, icon: Icon, hasTime, accent }) => (
              <KindTile
                key={kind}
                kind={kind}
                Icon={Icon}
                accent={accent}
                hasTime={hasTime}
                pref={getPref(kind)}
                setEnabled={(v) => setPrefEnabled(kind, v)}
                setTime={(h, m) => setPrefTime(kind, h, m)}
                busy={prefsBusy}
              />
            ))}
            {/* Sleep realignment — owner-only feature; its reminders fire
                at the plan's computed times, so no user time picker. */}
            {isOwner(user) && (
              <KindTile
                kind="sleep"
                Icon={Moon}
                accent="text-indigo-400"
                hasTime={false}
                pref={getPref('sleep')}
                setEnabled={(v) => setPrefEnabled('sleep', v)}
                setTime={() => {}}
                busy={prefsBusy}
              />
            )}
          </div>

          {/* Two diagnostic buttons:
                - "Send test" — full push pipeline (server → APNs/FCM → SW).
                - "Show local" — SW.showNotification directly, skipping push.
              Together they isolate where notifications break: if local
              works but send doesn't, it's push delivery. If neither
              works, it's an iOS-level block on the PWA.
              Hidden from non-owners — internal diagnostics shouldn't be
              in front of regular users. */}
          {showDebugButtons && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSendTest}
              disabled={testing || busy}
              className={cn(
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium',
                'bg-muted text-foreground hover:bg-accent transition-colors',
                (testing || busy) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {testing
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Send className="w-3 h-3" />}
              {t('reminders.sendTest')}
            </button>
            <button
              type="button"
              onClick={handleLocalTest}
              disabled={testing}
              className={cn(
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium',
                'bg-muted text-foreground hover:bg-accent transition-colors',
                testing && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Bell className="w-3 h-3" />
              {t('reminders.showLocal')}
            </button>
            {testResult && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] basis-full',
                  testResult.ok ? 'text-emerald-500' : 'text-red-500'
                )}
                dir="auto"
              >
                {testResult.ok && <Check className="w-3 h-3" />}
                {testResult.message}
              </span>
            )}
          </div>
          )}
        </>
      )}

      {/* Persistent hook error — only shown when there's no transient test
          result, otherwise the test result already carries the same info
          and we'd render the message twice. */}
      {error && !testResult && (
        <p className="mt-1 text-[11px] text-red-500" dir="auto">{error}</p>
      )}
    </div>
  )
}

// Single-line diagnostic indicator. Shows a state-coloured dot, a
// label, and a tiny detail string. Three states: 'ok' (green), 'bad'
// (red), 'pending' (muted) — for the "permission has never been
// asked" case which isn't a failure, just an empty starting point.
function DiagnosticRow({ label, state, detail }) {
  const dotClass = state === 'ok' ? 'bg-emerald-500'
    : state === 'bad' ? 'bg-red-500'
    : 'bg-muted-foreground/40'
  return (
    <div className="flex items-center gap-2 text-[11px]" dir="auto">
      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dotClass)} />
      <span className="text-muted-foreground">{label}</span>
      <span className="flex-1 min-w-0 text-foreground truncate">{detail}</span>
    </div>
  )
}

// One row per additional notification kind: icon + label + on/off toggle,
// plus an inline time picker when applicable. Kept inline rather than its
// own file because it's only used here and the prop surface is small.
function KindTile({ kind, Icon, accent, hasTime, pref, setEnabled, setTime, busy }) {
  const { t } = useTranslation()
  const [timeStr, setTimeStr] = useState(`${pad(pref.reminderHour)}:${pad(pref.reminderMinute)}`)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!editing) {
      setTimeStr(`${pad(pref.reminderHour)}:${pad(pref.reminderMinute)}`)
    }
  }, [pref.reminderHour, pref.reminderMinute, editing])

  const commitTime = async () => {
    setEditing(false)
    if (!pref.enabled) return
    const [h, m] = timeStr.split(':').map(Number)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return
    if (h === pref.reminderHour && m === pref.reminderMinute) return
    await setTime(h, m)
  }

  return (
    <div className="flex items-center gap-2.5 py-1">
      <Icon className={cn('w-4 h-4 flex-shrink-0', accent)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground" dir="auto">
          {t(`reminders.kinds.${kind}.title`)}
        </p>
        <p className="text-[10px] text-muted-foreground" dir="auto">
          {t(`reminders.kinds.${kind}.subtitle`)}
        </p>
      </div>
      {hasTime && pref.enabled && (
        <input
          type="time"
          value={timeStr}
          onFocus={() => setEditing(true)}
          onChange={(e) => setTimeStr(e.target.value)}
          onBlur={commitTime}
          disabled={busy}
          dir="ltr"
          className="h-7 px-2 rounded border border-input bg-background text-foreground font-mono text-[11px] focus:outline-none flex-shrink-0"
        />
      )}
      <button
        type="button"
        onClick={() => setEnabled(!pref.enabled)}
        disabled={busy}
        aria-label={pref.enabled
          ? t(`reminders.kinds.${kind}.disable`)
          : t(`reminders.kinds.${kind}.enable`)}
        className={cn(
          'relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0',
          pref.enabled ? 'bg-app-primary-500' : 'bg-muted',
          busy && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span
          className={cn(
            'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
            pref.enabled ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5 rtl:-translate-x-0.5'
          )}
        />
      </button>
    </div>
  )
}
